import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeftRight, Send, Wallet as WalletIcon, Coins } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, dateFmt } from "@/lib/format";

export default function Wallet() {
  const [wallet, setWallet] = useState({ deposit_balance: 0, income_balance: 0, points_balance: 0 });
  const [transfers, setTransfers] = useState([]);
  const [limits, setLimits] = useState(null);
  const [members, setMembers] = useState([]);
  const [showList, setShowList] = useState(false);
  const [convertAmt, setConvertAmt] = useState("");
  const [pointsAmt, setPointsAmt] = useState("");
  const [xfer, setXfer] = useState({ to_username: "", amount: "" });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [dash, tx, lim, dl] = await Promise.all([api.get("/me/dashboard"), api.get("/me/transfers"), api.get("/me/transfer-limits"), api.get("/me/downline")]);
    setWallet(dash.data.wallet);
    setTransfers(tx.data);
    setLimits(lim.data);
    setMembers(dl.data.members || []);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Load failed")); }, [load]);

  const convert = async () => {
    setBusy("convert");
    try {
      const { data } = await api.post("/me/income-to-deposit", { amount: Number(convertAmt) });
      toast.success(data.message);
      setConvertAmt("");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy("");
  };

  const convertPoints = async () => {
    setBusy("points");
    try {
      const { data } = await api.post("/me/points-to-deposit", { amount: Number(pointsAmt) });
      toast.success(data.message);
      setPointsAmt("");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy("");
  };

  const transfer = async () => {
    setBusy("transfer");
    try {
      const { data } = await api.post("/me/transfer", { to_username: xfer.to_username, amount: Number(xfer.amount) });
      toast.success(data.message);
      setXfer({ to_username: "", amount: "" });
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy("");
  };

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2"><WalletIcon className="w-6 h-6 text-amber-400" /> Wallet</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5" data-testid="wallet-deposit-balance">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Deposit Balance</div>
          <div className="font-mono-fin text-2xl font-bold gold-text mt-1">{money(wallet.deposit_balance)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Used for activation, packages & transfers · <b className="text-amber-300/80">not withdrawable</b></div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="wallet-income-balance">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Income Balance</div>
          <div className="font-mono-fin text-2xl font-bold text-emerald-400 mt-1">{money(wallet.income_balance)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Withdrawable · or convert to deposit</div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="wallet-points-balance">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Points Balance</div>
          <div className="font-mono-fin text-2xl font-bold text-sky-400 mt-1">{money(wallet.points_balance)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Convertible to deposit ($1 per point)</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Income -> Deposit */}
        <div className="glass rounded-2xl p-6" data-testid="convert-card">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><ArrowLeftRight className="w-5 h-5 text-emerald-400" /> Income → Deposit</h2>
          <p className="text-sm text-slate-400 mb-4">Move funds from your income balance into your deposit balance to reinvest or activate packages.</p>
          <label className="text-xs text-amber-400 uppercase tracking-wider">Amount (USDT)</label>
          <input data-testid="convert-amount-input" type="number" step="0.01" min="0" value={convertAmt} onChange={(e) => setConvertAmt(e.target.value)}
            placeholder="0.00" className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none text-sm" />
          <button data-testid="convert-submit-btn" disabled={busy === "convert" || !convertAmt || Number(convertAmt) <= 0} onClick={convert}
            className="mt-4 gold-btn w-full py-3 rounded-xl font-bold text-sm disabled:opacity-50">
            {busy === "convert" ? "Processing..." : "Move to Deposit"}
          </button>
        </div>

        {/* Points -> Deposit */}
        <div className="glass rounded-2xl p-6" data-testid="points-convert-card">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><Coins className="w-5 h-5 text-sky-400" /> Points → Deposit</h2>
          <p className="text-sm text-slate-400 mb-4">Convert your points to USDT deposit balance at <b>$1 per point</b>. Points can only be moved to deposit balance.</p>
          <label className="text-xs text-amber-400 uppercase tracking-wider">Points to convert</label>
          <input data-testid="points-amount-input" type="number" step="0.01" min="0" value={pointsAmt} onChange={(e) => setPointsAmt(e.target.value)}
            placeholder="0.00" className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none text-sm" />
          <button data-testid="points-submit-btn" disabled={busy === "points" || !pointsAmt || Number(pointsAmt) <= 0} onClick={convertPoints}
            className="mt-4 gold-btn w-full py-3 rounded-xl font-bold text-sm disabled:opacity-50">
            {busy === "points" ? "Processing..." : "Convert to Deposit"}
          </button>
        </div>

        {/* P2P transfer */}
        <div className="glass rounded-2xl p-6" data-testid="transfer-card">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><Send className="w-5 h-5 text-amber-400" /> Transfer to Downline</h2>
          <p className="text-sm text-slate-400 mb-4">Send deposit balance to a member <b>in your own downline</b> (by their username).</p>
          {limits && (limits.min_transfer > 0 || limits.daily_cap > 0) && (
            <div className="mb-4 text-xs rounded-xl bg-amber-500/10 border border-amber-500/20 p-3 text-amber-200" data-testid="transfer-limits-note">
              {limits.min_transfer > 0 && <div>Minimum transfer: <b>${limits.min_transfer.toLocaleString()}</b></div>}
              {limits.daily_cap > 0 && <div>Daily cap: <b>${limits.daily_cap.toLocaleString()}</b> · Sent today: <b>${limits.used_today.toLocaleString()}</b> · Remaining: <b>${(limits.remaining_today ?? 0).toLocaleString()}</b></div>}
            </div>
          )}
          <label className="text-xs text-amber-400 uppercase tracking-wider">Recipient Username</label>
          <div className="relative mt-1">
            <input data-testid="transfer-username-input" value={xfer.to_username}
              onChange={(e) => { setXfer({ ...xfer, to_username: e.target.value }); setShowList(true); }}
              onFocus={() => setShowList(true)}
              onBlur={() => setTimeout(() => setShowList(false), 150)}
              placeholder="Search your downline by name or username"
              className="w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none text-sm" autoComplete="off" />
            {showList && (() => {
              const term = xfer.to_username.trim().toLowerCase();
              const opts = members.filter((m) => !term || (m.username || "").toLowerCase().includes(term) || (m.name || "").toLowerCase().includes(term) || (m.referral_code || "").toLowerCase().includes(term)).slice(0, 8);
              return opts.length > 0 ? (
                <div data-testid="downline-dropdown" className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-xl bg-[#0D0E12] border border-amber-500/30 shadow-xl">
                  {opts.map((m) => (
                    <button type="button" key={m.id} data-testid={`downline-option-${m.username || m.id}`}
                      onMouseDown={(e) => { e.preventDefault(); setXfer({ ...xfer, to_username: m.username || m.referral_code }); setShowList(false); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-amber-500/10 flex items-center justify-between gap-2">
                      <span><span className="font-medium text-sm">{m.name}</span> <span className="text-xs text-slate-400">@{m.username || m.referral_code}</span></span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300">L{m.level}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div data-testid="downline-dropdown-empty" className="absolute z-20 mt-1 w-full rounded-xl bg-[#0D0E12] border border-amber-500/30 px-4 py-3 text-xs text-slate-500">
                  {members.length === 0 ? "You have no downline members yet." : "No matching downline member."}
                </div>
              );
            })()}
          </div>
          <label className="text-xs text-amber-400 uppercase tracking-wider mt-3 block">Amount (USDT)</label>
          <input data-testid="transfer-amount-input" type="number" step="0.01" min="0" value={xfer.amount} onChange={(e) => setXfer({ ...xfer, amount: e.target.value })}
            placeholder="0.00" className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none text-sm" />
          <button data-testid="transfer-submit-btn" disabled={busy === "transfer" || !xfer.to_username || !xfer.amount || Number(xfer.amount) <= 0} onClick={transfer}
            className="mt-4 gold-btn w-full py-3 rounded-xl font-bold text-sm disabled:opacity-50">
            {busy === "transfer" ? "Sending..." : "Send Transfer"}
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="transfer-history">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Coins className="w-5 h-5 text-amber-400" /> Transfer History</h3>
        {transfers.length === 0 ? (
          <p className="text-slate-500 text-sm">No transfers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
                <th className="text-left py-3">Direction</th><th className="text-left py-3">Counterparty</th><th className="text-right py-3">Amount</th><th className="text-right py-3">Date</th>
              </tr></thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.id} className="border-b border-slate-800/60">
                    <td className="py-3"><span className={`text-[10px] px-2 py-1 rounded-full uppercase ${t.direction === "in" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{t.direction === "in" ? "Received" : "Sent"}</span></td>
                    <td className="py-3 text-slate-300">{t.counterparty}</td>
                    <td className={`py-3 text-right font-mono-fin ${t.direction === "in" ? "text-emerald-400" : "text-red-400"}`}>{t.direction === "in" ? "+" : "−"}{money(t.amount)}</td>
                    <td className="py-3 text-right text-slate-500 text-xs">{dateFmt(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
